import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  increment,
  onSnapshot
} from 'firebase/firestore';
import { uploadFile, deleteFile, getFileDownloadURL } from './r2storage';
import { db } from './config';

// Collection names
const COLLECTIONS = {
  TUITION_TEACHERS: 'tuitionTeachers',
  TEACHER_BATCHES: 'teacherBatches',
  DEMO_BOOKINGS: 'demoBookings',
  TEACHER_HIRINGS: 'teacherHirings',
  BATCH_MEMBERS: 'batchMembers',
  BATCH_POSTS: 'batchPosts',
  BATCH_ASSIGNMENTS: 'batchAssignments',
  BATCH_DISCUSSIONS: 'batchDiscussions',
  ASSIGNMENT_SUBMISSIONS: 'assignmentSubmissions',
  TEACHER_NEGOTIATIONS: 'teacherNegotiations',
  NEGOTIATION_MESSAGES: 'negotiationMessages'
};

// Tuition Teacher Management
export const tuitionTeacherService = {
  // Register a new tuition teacher
  async registerTeacher(teacherData) {
    try {
      const docRef = await addDoc(collection(db, COLLECTIONS.TUITION_TEACHERS), {
        ...teacherData,
        status: 'pending', // pending, approved, rejected
        isActive: true,
        rating: 0,
        totalStudents: 0,
        totalClasses: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return docRef.id;
    } catch (error) {
      throw error;
    }
  },

  // Update teacher profile
  async updateTeacher(teacherId, updateData) {
    try {
      const teacherRef = doc(db, COLLECTIONS.TUITION_TEACHERS, teacherId);
      await updateDoc(teacherRef, {
        ...updateData,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      throw error;
    }
  },

  // Get teacher by ID
  async getTeacher(teacherId) {
    try {
      const teacherRef = doc(db, COLLECTIONS.TUITION_TEACHERS, teacherId);
      const teacherSnap = await getDoc(teacherRef);
      if (teacherSnap.exists()) {
        return { id: teacherSnap.id, ...teacherSnap.data() };
      }
      return null;
    } catch (error) {
      throw error;
    }
  },

  // Check if user is an approved teacher
  async isApprovedTeacher(userId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.TUITION_TEACHERS),
        where('userId', '==', userId),
        where('status', '==', 'approved')
      );
      
      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty;
    } catch (error) {
      return false;
    }
  },

  // Get teacher registration status for a user
  async getTeacherRegistrationStatus(userId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.TUITION_TEACHERS),
        where('userId', '==', userId)
      );
      
      const querySnapshot = await getDocs(q);
      if (querySnapshot.empty) {
        return { isRegistered: false, status: null };
      }
      
      const teacherDoc = querySnapshot.docs[0];
      const teacherData = { id: teacherDoc.id, ...teacherDoc.data() };
      
      return {
        isRegistered: true,
        status: teacherData.status,
        teacherId: teacherDoc.id,
        teacherData: teacherData
      };
    } catch (error) {
      return { isRegistered: false, status: null };
    }
  },

  // Search teachers with filters
  async searchTeachers(filters = {}) {
    try {
      // Start with basic query - get all teachers first, then filter in memory
      // This avoids complex composite index requirements
      let q = query(collection(db, COLLECTIONS.TUITION_TEACHERS));
      
      // Only apply status filter as it's the most important one
      if (filters.status) {
        q = query(q, where('status', '==', filters.status));
      } else {
        q = query(q, where('status', '==', 'approved'));
      }
      
      // Always filter by active status
      q = query(q, where('isActive', '==', true));
      
      if (filters.limit) {
        q = query(q, limit(filters.limit));
      }
      
      const querySnapshot = await getDocs(q);
      let teachers = [];
      querySnapshot.forEach((doc) => {
        teachers.push({ id: doc.id, ...doc.data() });
      });
      
      // Apply additional filters in memory to avoid complex indexes
      if (filters.subjects && filters.subjects.length > 0) {
        teachers = teachers.filter(teacher => 
          teacher.subjects && teacher.subjects.some(subject => 
            filters.subjects.includes(subject)
          )
        );
      }
      
      if (filters.classes && filters.classes.length > 0) {
        teachers = teachers.filter(teacher => 
          teacher.classes && teacher.classes.some(cls => 
            filters.classes.includes(cls)
          )
        );
      }
      
      if (filters.locality) {
        teachers = teachers.filter(teacher => 
          teacher.locality && teacher.locality.toLowerCase().includes(filters.locality.toLowerCase())
        );
      }
      
      if (filters.maxFees) {
        teachers = teachers.filter(teacher => 
          teacher.feesPerHour && teacher.feesPerHour <= parseInt(filters.maxFees)
        );
      }
      
      if (filters.availability && filters.availability.length > 0) {
        teachers = teachers.filter(teacher => 
          teacher.availability && teacher.availability.some(avail => 
            filters.availability.includes(avail)
          )
        );
      }
      
      // Sort in memory by rating (highest first)
      teachers.sort((a, b) => {
        const aRating = a.rating || 0;
        const bRating = b.rating || 0;
        return bRating - aRating;
      });
      
      return teachers;
    } catch (error) {
      throw error;
    }
  },

  // Upload teacher photo
  async uploadTeacherPhoto(teacherId, file) {
    try {
      const storagePath = `tuition-teachers/${teacherId}/photo`;
      const result = await uploadFile(file, storagePath);
      if (!result.success) {
        throw new Error(result.error || 'Upload failed');
      }
      return result.url;
    } catch (error) {
      throw error;
    }
  },

  // Upload teacher documents (Aadhar, PAN)
  async uploadTeacherDocument(teacherId, file, documentType) {
    try {
      const storagePath = `tuition-teachers/${teacherId}/documents/${documentType}`;
      const result = await uploadFile(file, storagePath);
      if (!result.success) {
        throw new Error(result.error || 'Upload failed');
      }
      return result.url;
    } catch (error) {
      throw error;
    }
  },

  // Get all teachers (for admin)
  async getAllTeachers() {
    try {
      // Simple query without ordering to avoid index requirements
      const q = query(collection(db, COLLECTIONS.TUITION_TEACHERS));
      
      const querySnapshot = await getDocs(q);
      const teachers = [];
      querySnapshot.forEach((doc) => {
        teachers.push({ id: doc.id, ...doc.data() });
      });
      
      // Sort in memory by creation date
      teachers.sort((a, b) => {
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return bTime - aTime; // Descending order
      });
      
      return teachers;
    } catch (error) {
      throw error;
    }
  },

  // Approve teacher
  async approveTeacher(teacherId) {
    try {
      const teacherRef = doc(db, COLLECTIONS.TUITION_TEACHERS, teacherId);
      const teacherDoc = await getDoc(teacherRef);
      
      if (!teacherDoc.exists()) {
        throw new Error('Teacher not found');
      }
      
      const teacherData = teacherDoc.data();
      
      // Update teacher status
      await updateDoc(teacherRef, {
        status: 'approved',
        approvedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      // Process referral if teacher has a referral code and it hasn't been processed yet
      if (teacherData.referralCode && !teacherData.referralProcessed && teacherData.userId) {
        try {
          // First, check if referral was already processed (for teachers registered with old logic)
          const { referralService } = await import('./referralService');
          const { collection, query, where, getDocs } = await import('firebase/firestore');
          
          // Check if this user was already referred
          // Import the referral service constants
          const COLLECTIONS_REFERRAL = {
            REFERRAL_EARNINGS: 'referralEarnings'
          };
          
          const existingReferralQuery = query(
            collection(db, COLLECTIONS_REFERRAL.REFERRAL_EARNINGS),
            where('referredUserId', '==', teacherData.userId)
          );
          const existingSnapshot = await getDocs(existingReferralQuery);
          
          if (!existingSnapshot.empty) {
            // Referral was already processed (probably from old registration logic)
            // Just mark it as processed in teacher document
            await updateDoc(teacherRef, {
              referralProcessed: true,
              referralProcessedAt: serverTimestamp(),
              referralProcessedNote: 'Already processed before approval logic update',
              updatedAt: serverTimestamp()
            });
            console.log('Referral was already processed for teacher:', teacherId, '- marked as processed');
          } else {
            // Referral not processed yet, process it now
            const referralResult = await referralService.processReferral(teacherData.userId, teacherData.referralCode);
            
            if (referralResult.success) {
              // Mark referral as processed
              await updateDoc(teacherRef, {
                referralProcessed: true,
                referralProcessedAt: serverTimestamp(),
                updatedAt: serverTimestamp()
              });
              console.log('Referral processed successfully for teacher:', teacherId);
            } else {
              console.warn('Referral processing failed for teacher:', teacherId, referralResult.error);
              // Still mark as processed to prevent retry attempts
              await updateDoc(teacherRef, {
                referralProcessed: true,
                referralProcessedAt: serverTimestamp(),
                referralProcessedNote: `Failed: ${referralResult.error}`,
                updatedAt: serverTimestamp()
              });
            }
          }
        } catch (referralError) {
          console.error('Error processing referral during teacher approval:', referralError);
          // Mark as processed to prevent retry attempts
          try {
            await updateDoc(teacherRef, {
              referralProcessed: true,
              referralProcessedAt: serverTimestamp(),
              referralProcessedNote: `Error: ${referralError.message}`,
              updatedAt: serverTimestamp()
            });
          } catch (updateError) {
            console.error('Failed to update referral processed flag:', updateError);
          }
          // Don't throw - teacher approval should still succeed even if referral fails
        }
      }
      
      return { success: true, teacherData };
    } catch (error) {
      throw error;
    }
  },

  // Reject teacher
  async rejectTeacher(teacherId, reason) {
    try {
      const teacherRef = doc(db, COLLECTIONS.TUITION_TEACHERS, teacherId);
      await updateDoc(teacherRef, {
        status: 'rejected',
        rejectionReason: reason,
        rejectedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      throw error;
    }
  },

  // Recalculate totalStudents count for a teacher (useful for fixing inconsistencies)
  async recalculateTotalStudents(teacherId) {
    try {
      // Get all active batches for this teacher
      const batchesQuery = query(
        collection(db, COLLECTIONS.TEACHER_BATCHES),
        where('teacherId', '==', teacherId),
        where('isActive', '==', true)
      );
      const batchesSnapshot = await getDocs(batchesQuery);
      
      // Collect all unique student IDs
      const uniqueStudentIds = new Set();
      
      for (const batchDoc of batchesSnapshot.docs) {
        const batchId = batchDoc.id;
        // Get all student members in this batch
        const membersQuery = query(
          collection(db, COLLECTIONS.BATCH_MEMBERS),
          where('batchId', '==', batchId),
          where('role', '==', 'student')
        );
        const membersSnapshot = await getDocs(membersQuery);
        
        membersSnapshot.forEach(memberDoc => {
          const memberData = memberDoc.data();
          const studentId = memberData.userId || memberData.studentId;
          if (studentId) {
            uniqueStudentIds.add(studentId);
          }
        });
      }
      
      // Update teacher's totalStudents count
      const teacherRef = doc(db, COLLECTIONS.TUITION_TEACHERS, teacherId);
      await updateDoc(teacherRef, {
        totalStudents: uniqueStudentIds.size,
        updatedAt: serverTimestamp()
      });
      
      return uniqueStudentIds.size;
    } catch (error) {
      throw error;
    }
  }
};

// Demo Class Booking Management
export const demoBookingService = {
  // Book a demo class
  async bookDemoClass(bookingData) {
    try {
      // Check if student already booked demo with this teacher
      const existingBooking = await this.getStudentDemoBooking(
        bookingData.studentId, 
        bookingData.teacherId
      );
      
      if (existingBooking) {
        throw new Error('You have already booked a demo class with this teacher');
      }
      
      // Validate time slots
      if (!bookingData.timeSlots || bookingData.timeSlots.length < 3) {
        throw new Error('Please provide 3 preferred time slots');
      }
      
      const docRef = await addDoc(collection(db, COLLECTIONS.DEMO_BOOKINGS), {
        ...bookingData,
        status: 'pending', // pending, confirmed, completed, cancelled
        selectedSlot: null, // Will be set by teacher when they confirm
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return docRef.id;
    } catch (error) {
      throw error;
    }
  },

  // Get student's demo booking for a specific teacher
  async getStudentDemoBooking(studentId, teacherId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.DEMO_BOOKINGS),
        where('studentId', '==', studentId),
        where('teacherId', '==', teacherId)
      );
      
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return { id: doc.id, ...doc.data() };
      }
      return null;
    } catch (error) {
      throw error;
    }
  },

  // Get all demo bookings for a student
  async getStudentDemoBookings(studentId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.DEMO_BOOKINGS),
        where('studentId', '==', studentId)
      );
      
      const querySnapshot = await getDocs(q);
      const bookings = [];
      querySnapshot.forEach((doc) => {
        bookings.push({ id: doc.id, ...doc.data() });
      });
      
      // Sort in memory by creation date
      bookings.sort((a, b) => {
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return bTime - aTime; // Descending order
      });
      
      return bookings;
    } catch (error) {
      throw error;
    }
  },

  // Get teacher's demo bookings
  async getTeacherDemoBookings(teacherId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.DEMO_BOOKINGS),
        where('teacherId', '==', teacherId)
      );
      
      const querySnapshot = await getDocs(q);
      const bookings = [];
      querySnapshot.forEach((doc) => {
        bookings.push({ id: doc.id, ...doc.data() });
      });
      
      // Sort in memory by creation date
      bookings.sort((a, b) => {
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return bTime - aTime; // Descending order
      });
      
      return bookings;
    } catch (error) {
      throw error;
    }
  },

  // Update demo booking status
  async updateDemoBookingStatus(bookingId, status) {
    try {
      const bookingRef = doc(db, COLLECTIONS.DEMO_BOOKINGS, bookingId);
      await updateDoc(bookingRef, {
        status,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      throw error;
    }
  },

  // Confirm demo booking by selecting a time slot
  async confirmDemoBooking(bookingId, selectedSlotIndex) {
    try {
      const bookingRef = doc(db, COLLECTIONS.DEMO_BOOKINGS, bookingId);
      const bookingDoc = await getDoc(bookingRef);
      
      if (!bookingDoc.exists()) {
        throw new Error('Demo booking not found');
      }
      
      const bookingData = bookingDoc.data();
      const selectedSlot = bookingData.timeSlots[selectedSlotIndex];
      
      if (!selectedSlot) {
        throw new Error('Invalid time slot selected');
      }
      
      await updateDoc(bookingRef, {
        status: 'confirmed',
        selectedSlot: selectedSlot,
        selectedSlotIndex: selectedSlotIndex,
        confirmedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      throw error;
    }
  }
};

// Teacher Hiring Management
export const teacherHiringService = {
  // Hire a teacher
  async hireTeacher(hiringData) {
    try {
      const docRef = await addDoc(collection(db, COLLECTIONS.TEACHER_HIRINGS), {
        ...hiringData,
        status: 'active', // active, completed, cancelled
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      // Batch creation is now manual - teacher will create batches when they want
      // Removed automatic batch creation
      
      return { hiringId: docRef.id };
    } catch (error) {
      throw error;
    }
  },

  // Create teacher batch from hiring (internal use)
  async createTeacherBatchFromHiring(teacherId, studentId, hiringId, studentData = {}) {
    try {
      const batchRef = await addDoc(collection(db, COLLECTIONS.TEACHER_BATCHES), {
        teacherId,
        studentId,
        hiringId,
        batchName: `Batch - ${new Date().toLocaleDateString()}`,
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      // Add teacher and student as batch members
      await this.addBatchMember(batchRef.id, teacherId, 'teacher');
      await this.addBatchMember(batchRef.id, studentId, 'student', {
        studentName: studentData.studentName,
        studentEmail: studentData.studentEmail
      });
      
      return batchRef.id;
    } catch (error) {
      throw error;
    }
  },

  // Add member to batch
  async addBatchMember(batchId, userId, role, userData = {}) {
    try {
      const memberRef = await addDoc(collection(db, COLLECTIONS.BATCH_MEMBERS), {
        batchId,
        userId,
        role, // teacher, student
        ...userData, // Include additional user data like name, email
        joinedAt: serverTimestamp()
      });
      
      // If adding a student, update teacher's totalStudents count
      if (role === 'student') {
        try {
          // Get batch details to find teacherId
          const batchDetails = await this.getBatchDetails(batchId);
          const teacherId = batchDetails.teacherId;
          
          if (teacherId) {
            // Check if this student is already in any of the teacher's batches
            const teacherBatches = await this.getTeacherBatchesByTeacherId(teacherId);
            let isNewStudent = true;
            
            for (const batch of teacherBatches) {
              if (batch.id === batchId) continue; // Skip current batch
              const members = await this.getBatchMembers(batch.id);
              const studentExists = members.some(m => 
                (m.userId === userId || m.studentId === userId) && m.role === 'student'
              );
              if (studentExists) {
                isNewStudent = false;
                break;
              }
            }
            
            // Only increment if this is a new student for the teacher
            if (isNewStudent) {
              const teacherRef = doc(db, COLLECTIONS.TUITION_TEACHERS, teacherId);
              await updateDoc(teacherRef, {
                totalStudents: increment(1),
                updatedAt: serverTimestamp()
              });
            }
          }
        } catch (updateError) {
          // Log error but don't fail the member addition
          console.error('Failed to update teacher totalStudents:', updateError);
        }
      }
      
      return memberRef.id;
    } catch (error) {
      throw error;
    }
  },

  // Get teacher's batches
  async getTeacherBatches(userId) {
    try {
      // First, we need to find the teacher's document ID from their userId
      const teacherQuery = query(
        collection(db, COLLECTIONS.TUITION_TEACHERS),
        where('userId', '==', userId)
      );
      const teacherSnapshot = await getDocs(teacherQuery);
      
      if (teacherSnapshot.empty) {
        return [];
      }
      
      const teacherDoc = teacherSnapshot.docs[0];
      const teacherId = teacherDoc.id; // This is the document ID used in batch records
      
      // Now query batches using the teacher's document ID
      const q = query(
        collection(db, COLLECTIONS.TEACHER_BATCHES),
        where('teacherId', '==', teacherId),
        where('isActive', '==', true)
      );
      
      const querySnapshot = await getDocs(q);
      const batches = [];
      querySnapshot.forEach((doc) => {
        batches.push({ id: doc.id, ...doc.data() });
      });
      
      // Sort in memory by creation date
      batches.sort((a, b) => {
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return bTime - aTime; // Descending order
      });
      
      return batches;
    } catch (error) {
      throw error;
    }
  },

  // Get teacher's batches by teacherId (internal helper)
  async getTeacherBatchesByTeacherId(teacherId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.TEACHER_BATCHES),
        where('teacherId', '==', teacherId),
        where('isActive', '==', true)
      );
      
      const querySnapshot = await getDocs(q);
      const batches = [];
      querySnapshot.forEach((doc) => {
        batches.push({ id: doc.id, ...doc.data() });
      });
      
      return batches;
    } catch (error) {
      return [];
    }
  },

  // Get batch details
  async getBatchDetails(batchId) {
    try {
      const batchRef = doc(db, COLLECTIONS.TEACHER_BATCHES, batchId);
      const batchSnap = await getDoc(batchRef);
      
      if (batchSnap.exists()) {
        return { id: batchSnap.id, ...batchSnap.data() };
      } else {
        throw new Error('Batch not found');
      }
    } catch (error) {
      throw error;
    }
  },

  // Get student's batches
  async getStudentBatches(studentId) {
    try {
      
      // Method 1: Get batches where studentId field exists (from hiring)
      const directBatchesQuery = query(
        collection(db, COLLECTIONS.TEACHER_BATCHES),
        where('studentId', '==', studentId),
        where('isActive', '==', true)
      );
      
      const directBatchesSnapshot = await getDocs(directBatchesQuery);
      const directBatches = [];
      directBatchesSnapshot.forEach((doc) => {
        directBatches.push({ id: doc.id, ...doc.data() });
      });
      
      
      // Method 2: Get batches where student is a member (from manual addition)
      const memberBatchesQuery = query(
        collection(db, COLLECTIONS.BATCH_MEMBERS),
        where('userId', '==', studentId),
        where('role', '==', 'student')
      );
      
      const memberBatchesSnapshot = await getDocs(memberBatchesQuery);
      const memberBatchIds = [];
      memberBatchesSnapshot.forEach((doc) => {
        memberBatchIds.push(doc.data().batchId);
      });
      
      
      // Get batch details for member batches
      const memberBatches = [];
      for (const batchId of memberBatchIds) {
        try {
          const batchRef = doc(db, COLLECTIONS.TEACHER_BATCHES, batchId);
          const batchSnap = await getDoc(batchRef);
          if (batchSnap.exists() && batchSnap.data().isActive) {
            memberBatches.push({ id: batchSnap.id, ...batchSnap.data() });
          }
        } catch (error) {
        }
      }
      
      
      // Combine and deduplicate batches
      const allBatches = [...directBatches];
      memberBatches.forEach(memberBatch => {
        if (!allBatches.find(batch => batch.id === memberBatch.id)) {
          allBatches.push(memberBatch);
        }
      });
      
      
      // Sort in memory
      allBatches.sort((a, b) => {
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return bTime - aTime;
      });
      
      return allBatches;
    } catch (error) {
      throw error;
    }
  },

  // Get teacher's hired students
  async getTeacherHiredStudents(userId) {
    try {
      
      // First, we need to find the teacher's document ID from their userId
      const teacherQuery = query(
        collection(db, COLLECTIONS.TUITION_TEACHERS),
        where('userId', '==', userId)
      );
      const teacherSnapshot = await getDocs(teacherQuery);
      
      if (teacherSnapshot.empty) {
        return [];
      }
      
      const teacherDoc = teacherSnapshot.docs[0];
      const teacherId = teacherDoc.id; // This is the document ID used in hiring records
      
      
      // Now query hiring records using the teacher's document ID
      const hiringsQuery = query(
        collection(db, COLLECTIONS.TEACHER_HIRINGS),
        where('teacherId', '==', teacherId)
      );
      const hiringsSnapshot = await getDocs(hiringsQuery);
      const allHirings = hiringsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      
      // Filter for active hirings
      const activeHirings = allHirings.filter(hiring => 
        hiring.status === 'active' || 
        hiring.status === 'hired' || 
        hiring.status === 'confirmed' ||
        !hiring.status // Include if no status field
      );
      
      return activeHirings;
    } catch (error) {
      throw error;
    }
  },

  // Get student's hired teachers
  async getStudentHiredTeachers(studentId) {
    try {
      
      // Get all hirings for this student
      const hiringsQuery = query(
        collection(db, COLLECTIONS.TEACHER_HIRINGS),
        where('studentId', '==', studentId)
      );
      
      const hiringsSnapshot = await getDocs(hiringsQuery);
      const hirings = hiringsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Filter for active hirings
      const activeHirings = hirings.filter(hiring => 
        hiring.status === 'active' || 
        hiring.status === 'hired' || 
        hiring.status === 'confirmed' ||
        !hiring.status // Include if no status field
      );
      
      // Get teacher details for each hiring
      const hiredTeachers = [];
      for (const hiring of activeHirings) {
        try {
          const teacher = await tuitionTeacherService.getTeacher(hiring.teacherId);
          if (teacher) {
            hiredTeachers.push({
              ...teacher,
              hiringId: hiring.id,
              hiringData: hiring
            });
          }
        } catch (error) {
        }
      }
      
      return hiredTeachers;
    } catch (error) {
      throw error;
    }
  },

  // Get teacher's batch limit (3 free + purchased batches)
  async getTeacherBatchLimit(userId) {
    try {
      // Get user profile to check purchased batches
      const { getUserProfile } = await import('./collections');
      const profileResult = await getUserProfile(userId);
      
      if (!profileResult.success || !profileResult.data) {
        return 3; // Default to 3 free batches
      }
      
      const userProfile = profileResult.data;
      const freeBatches = 3;
      const purchasedBatches = userProfile.purchasedBatches || 0; // Number of batches purchased (each purchase gives 3 batches)
      const totalLimit = freeBatches + (purchasedBatches * 3);
      
      return totalLimit;
    } catch (error) {
      // Default to 3 if error
      return 3;
    }
  },

  // Create a new batch for teacher (takes userId from Firebase Auth)
  async createTeacherBatch(userId, batchData) {
    try {
      // First, find the teacher's document ID from their userId
      const teacherQuery = query(
        collection(db, COLLECTIONS.TUITION_TEACHERS),
        where('userId', '==', userId)
      );
      const teacherSnapshot = await getDocs(teacherQuery);
      
      if (teacherSnapshot.empty) {
        throw new Error('Teacher not found. Please register as a teacher first.');
      }
      
      const teacherDoc = teacherSnapshot.docs[0];
      const teacherId = teacherDoc.id; // This is the document ID used in batch records
      
      // Check batch limit (3 free + purchased batches)
      const existingBatches = await this.getTeacherBatches(userId);
      const batchLimit = await this.getTeacherBatchLimit(userId);
      
      if (existingBatches.length >= batchLimit) {
        throw new Error(`You have reached your batch limit of ${batchLimit}. Purchase additional batches to create more.`);
      }
      
      // Create the batch with the teacher document ID
      const docRef = await addDoc(collection(db, COLLECTIONS.TEACHER_BATCHES), {
        ...batchData,
        teacherId, // Use the teacher document ID, not the userId
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      // Add teacher as a batch member (using teacher document ID)
      try {
        await this.addBatchMember(docRef.id, teacherId, 'teacher', {
          teacherName: teacherDoc.data().name || teacherDoc.data().email || teacherDoc.data().userId
        });
      } catch (memberError) {
        // Log but don't fail batch creation if member addition fails
      }
      
      return docRef.id;
    } catch (error) {
      throw error;
    }
  },

  // Purchase additional batches (3 batches for ₹100)
  async purchaseAdditionalBatches(userId, paymentTransactionId) {
    try {
      const { getUserProfile, updateUserProfile } = await import('./collections');
      
      // Get current user profile
      const profileResult = await getUserProfile(userId);
      if (!profileResult.success || !profileResult.data) {
        throw new Error('User profile not found');
      }
      
      const userProfile = profileResult.data;
      const currentPurchasedBatches = userProfile.purchasedBatches || 0;
      
      // Update purchased batches count
      await updateUserProfile(userId, {
        purchasedBatches: currentPurchasedBatches + 1,
        lastBatchPurchaseDate: serverTimestamp(),
        lastBatchPurchaseTransactionId: paymentTransactionId
      });
      
      return { success: true, newLimit: 3 + ((currentPurchasedBatches + 1) * 3) };
    } catch (error) {
      throw error;
    }
  },

  // Add student to batch
  async addStudentToBatch(batchId, studentId, studentData) {
    try {
      const docRef = await addDoc(collection(db, COLLECTIONS.BATCH_MEMBERS), {
        batchId,
        studentId,
        userId: studentId, // Also set userId for consistency
        studentName: studentData.name,
        studentEmail: studentData.email,
        role: 'student',
        joinedAt: serverTimestamp()
      });
      
      // Update teacher's totalStudents count
      try {
        // Get batch details to find teacherId
        const batchDetails = await this.getBatchDetails(batchId);
        const teacherId = batchDetails.teacherId;
        
        if (teacherId) {
          // Check if this student is already in any of the teacher's batches
          const teacherBatches = await this.getTeacherBatchesByTeacherId(teacherId);
          let isNewStudent = true;
          
          for (const batch of teacherBatches) {
            if (batch.id === batchId) continue; // Skip current batch
            const members = await this.getBatchMembers(batch.id);
            const studentExists = members.some(m => 
              (m.userId === studentId || m.studentId === studentId) && m.role === 'student'
            );
            if (studentExists) {
              isNewStudent = false;
              break;
            }
          }
          
          // Only increment if this is a new student for the teacher
          if (isNewStudent) {
            const teacherRef = doc(db, COLLECTIONS.TUITION_TEACHERS, teacherId);
            await updateDoc(teacherRef, {
              totalStudents: increment(1),
              updatedAt: serverTimestamp()
            });
          }
        }
      } catch (updateError) {
        // Log error but don't fail the student addition
        console.error('Failed to update teacher totalStudents:', updateError);
      }
      
      return docRef.id;
    } catch (error) {
      throw error;
    }
  },

  // Get batch members
  async getBatchMembers(batchId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.BATCH_MEMBERS),
        where('batchId', '==', batchId)
      );
      
      const querySnapshot = await getDocs(q);
      const members = [];
      querySnapshot.forEach((doc) => {
        members.push({ id: doc.id, ...doc.data() });
      });
      
      return members;
    } catch (error) {
      throw error;
    }
  },

  // Remove student from batch
  async removeStudentFromBatch(memberId) {
    try {
      // Get member details before deleting
      const memberRef = doc(db, COLLECTIONS.BATCH_MEMBERS, memberId);
      const memberSnap = await getDoc(memberRef);
      
      if (!memberSnap.exists()) {
        throw new Error('Member not found');
      }
      
      const memberData = memberSnap.data();
      const batchId = memberData.batchId;
      const userId = memberData.userId || memberData.studentId;
      const role = memberData.role;
      
      // Delete the member
      await deleteDoc(memberRef);
      
      // If removing a student, update teacher's totalStudents count
      if (role === 'student' && userId) {
        try {
          // Get batch details to find teacherId
          const batchDetails = await this.getBatchDetails(batchId);
          const teacherId = batchDetails.teacherId;
          
          if (teacherId) {
            // Check if this student is still in any other batch of the teacher
            const teacherBatches = await this.getTeacherBatchesByTeacherId(teacherId);
            let studentStillInOtherBatches = false;
            
            for (const batch of teacherBatches) {
              if (batch.id === batchId) continue; // Skip current batch (member already deleted)
              const members = await this.getBatchMembers(batch.id);
              const studentExists = members.some(m => 
                (m.userId === userId || m.studentId === userId) && m.role === 'student'
              );
              if (studentExists) {
                studentStillInOtherBatches = true;
                break;
              }
            }
            
            // Only decrement if student is not in any other batch of the teacher
            if (!studentStillInOtherBatches) {
              const teacherRef = doc(db, COLLECTIONS.TUITION_TEACHERS, teacherId);
              await updateDoc(teacherRef, {
                totalStudents: increment(-1),
                updatedAt: serverTimestamp()
              });
            }
          }
        } catch (updateError) {
          // Log error but don't fail the member removal
          console.error('Failed to update teacher totalStudents:', updateError);
        }
      }
    } catch (error) {
      throw error;
    }
  },

  // Update batch member
  async updateBatchMember(memberId, updateData) {
    try {
      const memberRef = doc(db, COLLECTIONS.BATCH_MEMBERS, memberId);
      await updateDoc(memberRef, {
        ...updateData,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      throw error;
    }
  },

  // Delete teacher batch and all associated data
  async deleteTeacherBatch(batchId, userId) {
    try {
      // Verify the batch exists and belongs to the teacher
      const batchDetails = await this.getBatchDetails(batchId);
      
      // Get teacher document ID from userId
      const teacherQuery = query(
        collection(db, COLLECTIONS.TUITION_TEACHERS),
        where('userId', '==', userId)
      );
      const teacherSnapshot = await getDocs(teacherQuery);
      
      if (teacherSnapshot.empty) {
        throw new Error('Teacher not found');
      }
      
      const teacherDoc = teacherSnapshot.docs[0];
      const teacherId = teacherDoc.id;
      
      // Verify the batch belongs to this teacher
      if (batchDetails.teacherId !== teacherId) {
        throw new Error('You do not have permission to delete this batch');
      }
      
      // Get all batch members and delete them
      const membersQuery = query(
        collection(db, COLLECTIONS.BATCH_MEMBERS),
        where('batchId', '==', batchId)
      );
      const membersSnapshot = await getDocs(membersQuery);
      const memberDeletions = [];
      membersSnapshot.forEach((memberDoc) => {
        memberDeletions.push(deleteDoc(memberDoc.ref));
      });

      // Get all batch posts and delete them (and their files)
      const postsQuery = query(
        collection(db, COLLECTIONS.BATCH_POSTS),
        where('batchId', '==', batchId)
      );
      const postsSnapshot = await getDocs(postsQuery);
      const postDeletions = [];
      postsSnapshot.forEach((postDoc) => {
        const postData = postDoc.data();
        // Delete attached files from storage
        if (postData.attachments && Array.isArray(postData.attachments)) {
          postData.attachments.forEach((file) => {
            if (file.path) {
              try {
                deleteFile(file.path).catch(() => {
                  // Ignore errors if file doesn't exist
                });
              } catch (error) {
                // Ignore storage errors
              }
            }
          });
        }
        postDeletions.push(deleteDoc(postDoc.ref));
      });

      // Get all batch assignments and delete them (and their files and submissions)
      const assignmentsQuery = query(
        collection(db, COLLECTIONS.BATCH_ASSIGNMENTS),
        where('batchId', '==', batchId)
      );
      const assignmentsSnapshot = await getDocs(assignmentsQuery);
      const assignmentDeletions = [];
      const submissionDeletions = [];
      const assignmentIds = [];
      
      // Collect assignment IDs first
      assignmentsSnapshot.forEach((assignmentDoc) => {
        assignmentIds.push(assignmentDoc.id);
        const assignmentData = assignmentDoc.data();
        // Delete attached files from storage
        if (assignmentData.attachments && Array.isArray(assignmentData.attachments)) {
          assignmentData.attachments.forEach((file) => {
            if (file.path) {
              try {
                deleteFile(file.path).catch(() => {
                  // Ignore errors if file doesn't exist
                });
              } catch (error) {
                // Ignore storage errors
              }
            }
          });
        }
        assignmentDeletions.push(deleteDoc(assignmentDoc.ref));
      });
      
      // Get all submissions for all assignments
      if (assignmentIds.length > 0) {
        const allSubmissionsPromises = assignmentIds.map(async (assignmentId) => {
          const submissionsQuery = query(
            collection(db, COLLECTIONS.ASSIGNMENT_SUBMISSIONS),
            where('assignmentId', '==', assignmentId)
          );
          const submissionsSnapshot = await getDocs(submissionsQuery);
          const submissionDocs = [];
          submissionsSnapshot.forEach((submissionDoc) => {
            submissionDocs.push(submissionDoc);
          });
          return submissionDocs;
        });
        
        const allSubmissionsArrays = await Promise.all(allSubmissionsPromises);
        allSubmissionsArrays.flat().forEach((submissionDoc) => {
          const submissionData = submissionDoc.data();
          // Delete submission files from storage
          if (submissionData.files && Array.isArray(submissionData.files)) {
            submissionData.files.forEach((file) => {
              if (file.path) {
                try {
                  deleteFile(file.path).catch(() => {
                    // Ignore errors if file doesn't exist
                  });
                } catch (error) {
                  // Ignore storage errors
                }
              }
            });
          }
          submissionDeletions.push(deleteDoc(submissionDoc.ref));
        });
      }

      // Get all batch discussions and delete them
      const discussionsQuery = query(
        collection(db, COLLECTIONS.BATCH_DISCUSSIONS),
        where('batchId', '==', batchId)
      );
      const discussionsSnapshot = await getDocs(discussionsQuery);
      const discussionDeletions = [];
      discussionsSnapshot.forEach((discussionDoc) => {
        discussionDeletions.push(deleteDoc(discussionDoc.ref));
      });

      // Wait for all deletions to complete
      await Promise.all([
        ...memberDeletions,
        ...postDeletions,
        ...assignmentDeletions,
        ...discussionDeletions,
        ...submissionDeletions
      ]);

      // Finally, delete the batch document itself
      const batchRef = doc(db, COLLECTIONS.TEACHER_BATCHES, batchId);
      await deleteDoc(batchRef);

      return { success: true };
    } catch (error) {
      throw error;
    }
  }
};

// Batch Content Management
export const batchContentService = {
  // Create batch post
  async createBatchPost(batchId, postData) {
    try {
      const docRef = await addDoc(collection(db, COLLECTIONS.BATCH_POSTS), {
        ...postData,
        batchId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return docRef.id;
    } catch (error) {
      throw error;
    }
  },

  // Create batch assignment
  async createBatchAssignment(batchId, assignmentData) {
    try {
      const docRef = await addDoc(collection(db, COLLECTIONS.BATCH_ASSIGNMENTS), {
        ...assignmentData,
        batchId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return docRef.id;
    } catch (error) {
      throw error;
    }
  },

  // Create batch discussion
  async createBatchDiscussion(batchId, discussionData) {
    try {
      const docRef = await addDoc(collection(db, COLLECTIONS.BATCH_DISCUSSIONS), {
        ...discussionData,
        batchId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return docRef.id;
    } catch (error) {
      throw error;
    }
  },

  // Get batch posts
  async getBatchPosts(batchId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.BATCH_POSTS),
        where('batchId', '==', batchId)
      );
      
      const querySnapshot = await getDocs(q);
      const posts = [];
      querySnapshot.forEach((doc) => {
        posts.push({ id: doc.id, ...doc.data() });
      });
      
      // Sort in memory by creation date (newest first)
      posts.sort((a, b) => {
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return bTime - aTime;
      });
      
      return posts;
    } catch (error) {
      throw error;
    }
  },

  // Get batch assignments
  async getBatchAssignments(batchId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.BATCH_ASSIGNMENTS),
        where('batchId', '==', batchId)
      );
      
      const querySnapshot = await getDocs(q);
      const assignments = [];
      querySnapshot.forEach((doc) => {
        assignments.push({ id: doc.id, ...doc.data() });
      });
      
      // Sort in memory by creation date (newest first)
      assignments.sort((a, b) => {
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return bTime - aTime;
      });
      
      return assignments;
    } catch (error) {
      throw error;
    }
  },

  // Get batch discussions
  async getBatchDiscussions(batchId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.BATCH_DISCUSSIONS),
        where('batchId', '==', batchId)
      );
      
      const querySnapshot = await getDocs(q);
      const discussions = [];
      querySnapshot.forEach((doc) => {
        discussions.push({ id: doc.id, ...doc.data() });
      });
      
      // Sort in memory by creation date (newest first)
      discussions.sort((a, b) => {
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return bTime - aTime;
      });
      
      return discussions;
    } catch (error) {
      throw error;
    }
  },

  // Update batch post
  async updateBatchPost(postId, updateData) {
    try {
      const postRef = doc(db, COLLECTIONS.BATCH_POSTS, postId);
      await updateDoc(postRef, {
        ...updateData,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      throw error;
    }
  },

  // Update batch assignment
  async updateBatchAssignment(assignmentId, updateData) {
    try {
      const assignmentRef = doc(db, COLLECTIONS.BATCH_ASSIGNMENTS, assignmentId);
      await updateDoc(assignmentRef, {
        ...updateData,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      throw error;
    }
  },

  // Update batch discussion
  async updateBatchDiscussion(discussionId, updateData) {
    try {
      const discussionRef = doc(db, COLLECTIONS.BATCH_DISCUSSIONS, discussionId);
      await updateDoc(discussionRef, {
        ...updateData,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      throw error;
    }
  },

  // Delete batch post
  async deleteBatchPost(postId) {
    try {
      await deleteDoc(doc(db, COLLECTIONS.BATCH_POSTS, postId));
    } catch (error) {
      throw error;
    }
  },

  // Delete batch assignment
  async deleteBatchAssignment(assignmentId) {
    try {
      await deleteDoc(doc(db, COLLECTIONS.BATCH_ASSIGNMENTS, assignmentId));
    } catch (error) {
      throw error;
    }
  },

  // Delete batch discussion
  async deleteBatchDiscussion(discussionId) {
    try {
      await deleteDoc(doc(db, COLLECTIONS.BATCH_DISCUSSIONS, discussionId));
    } catch (error) {
      throw error;
    }
  },

  // Upload file for batch content (post or assignment)
  async uploadBatchFile(batchId, file, contentType = 'post') {
    try {
      const fileName = `${Date.now()}_${file.name}`;
      const storagePath = `batch-content/${batchId}/${contentType}/${fileName}`;
      
      const result = await uploadFile(file, storagePath);
      if (!result.success) {
        throw new Error(result.error || 'Upload failed');
      }
      
      return {
        url: result.url,
        path: storagePath,
        name: file.name,
        size: file.size,
        type: file.type,
        uploadedAt: new Date().toISOString()
      };
    } catch (error) {
      throw error;
    }
  },

  // Delete file from storage
  async deleteBatchFile(filePath) {
    try {
      const result = await deleteFile(filePath);
      if (!result.success) {
        throw new Error(result.error || 'Delete failed');
      }
      return true;
    } catch (error) {
      throw error;
    }
  },

  // Update post with files
  async updatePostFiles(postId, files) {
    try {
      const postRef = doc(db, COLLECTIONS.BATCH_POSTS, postId);
      await updateDoc(postRef, {
        attachments: files,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      throw error;
    }
  },

  // Update assignment with files
  async updateAssignmentFiles(assignmentId, files) {
    try {
      const assignmentRef = doc(db, COLLECTIONS.BATCH_ASSIGNMENTS, assignmentId);
      await updateDoc(assignmentRef, {
        attachments: files,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      throw error;
    }
  },

  // Submit assignment (for students)
  async submitAssignment(assignmentId, studentId, submissionData) {
    try {
      // Check if already submitted
      const existingSubmission = await this.getStudentSubmission(assignmentId, studentId);
      if (existingSubmission) {
        // Update existing submission
        const submissionRef = doc(db, COLLECTIONS.ASSIGNMENT_SUBMISSIONS, existingSubmission.id);
        await updateDoc(submissionRef, {
          ...submissionData,
          updatedAt: serverTimestamp(),
          submittedAt: serverTimestamp()
        });
        return existingSubmission.id;
      } else {
        // Create new submission
        const docRef = await addDoc(collection(db, COLLECTIONS.ASSIGNMENT_SUBMISSIONS), {
          assignmentId,
          studentId,
          ...submissionData,
          status: 'submitted',
          submittedAt: serverTimestamp(),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        return docRef.id;
      }
    } catch (error) {
      throw error;
    }
  },

  // Get student's submission for an assignment
  async getStudentSubmission(assignmentId, studentId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.ASSIGNMENT_SUBMISSIONS),
        where('assignmentId', '==', assignmentId),
        where('studentId', '==', studentId)
      );
      
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return { id: doc.id, ...doc.data() };
      }
      return null;
    } catch (error) {
      throw error;
    }
  },

  // Get all submissions for an assignment
  async getAssignmentSubmissions(assignmentId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.ASSIGNMENT_SUBMISSIONS),
        where('assignmentId', '==', assignmentId)
      );
      
      const querySnapshot = await getDocs(q);
      const submissions = [];
      querySnapshot.forEach((doc) => {
        submissions.push({ id: doc.id, ...doc.data() });
      });
      
      // Sort by submission date (newest first)
      submissions.sort((a, b) => {
        const aTime = a.submittedAt?.seconds || 0;
        const bTime = b.submittedAt?.seconds || 0;
        return bTime - aTime;
      });
      
      return submissions;
    } catch (error) {
      throw error;
    }
  },

  // Get assignment submission statistics
  async getAssignmentStats(assignmentId, batchId) {
    try {
      // Get all batch members (students)
      const members = await teacherHiringService.getBatchMembers(batchId);
      const students = members.filter(m => m.role === 'student');
      
      // Get all submissions
      const submissions = await this.getAssignmentSubmissions(assignmentId);
      
      const submittedStudentIds = new Set(submissions.map(s => s.studentId));
      
      const submitted = students.filter(s => submittedStudentIds.has(s.studentId || s.userId));
      const remaining = students.filter(s => !submittedStudentIds.has(s.studentId || s.userId));
      
      return {
        total: students.length,
        submitted: submitted.length,
        remaining: remaining.length,
        submittedStudents: submitted,
        remainingStudents: remaining,
        submissions: submissions
      };
    } catch (error) {
      throw error;
    }
  },

  // Delete assignment submission
  async deleteSubmission(submissionId) {
    try {
      await deleteDoc(doc(db, COLLECTIONS.ASSIGNMENT_SUBMISSIONS, submissionId));
    } catch (error) {
      throw error;
    }
  }
};

// Teacher Negotiation Management
export const teacherNegotiationService = {
  // Create a new negotiation proposal
  async createNegotiation(negotiationData) {
    try {
      const docRef = await addDoc(collection(db, COLLECTIONS.TEACHER_NEGOTIATIONS), {
        ...negotiationData,
        status: 'pending', // pending, active, accepted, rejected, closed
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return docRef.id;
    } catch (error) {
      throw error;
    }
  },

  // Get student's negotiations
  async getStudentNegotiations(studentId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.TEACHER_NEGOTIATIONS),
        where('studentId', '==', studentId)
      );
      
      const querySnapshot = await getDocs(q);
      const negotiations = [];
      querySnapshot.forEach((doc) => {
        negotiations.push({ id: doc.id, ...doc.data() });
      });
      
      // Sort by creation date (newest first)
      negotiations.sort((a, b) => {
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return bTime - aTime;
      });
      
      return negotiations;
    } catch (error) {
      throw error;
    }
  },

  // Get teacher's negotiations
  async getTeacherNegotiations(teacherId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.TEACHER_NEGOTIATIONS),
        where('teacherId', '==', teacherId)
      );
      
      const querySnapshot = await getDocs(q);
      const negotiations = [];
      querySnapshot.forEach((doc) => {
        negotiations.push({ id: doc.id, ...doc.data() });
      });
      
      // Sort by creation date (newest first)
      negotiations.sort((a, b) => {
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return bTime - aTime;
      });
      
      return negotiations;
    } catch (error) {
      throw error;
    }
  },

  // Get negotiation by ID
  async getNegotiation(negotiationId) {
    try {
      const negotiationRef = doc(db, COLLECTIONS.TEACHER_NEGOTIATIONS, negotiationId);
      const negotiationSnap = await getDoc(negotiationRef);
      
      if (negotiationSnap.exists()) {
        return { id: negotiationSnap.id, ...negotiationSnap.data() };
      }
      return null;
    } catch (error) {
      throw error;
    }
  },

  // Update negotiation (e.g., update proposed price)
  async updateNegotiation(negotiationId, updateData) {
    try {
      const negotiationRef = doc(db, COLLECTIONS.TEACHER_NEGOTIATIONS, negotiationId);
      await updateDoc(negotiationRef, {
        ...updateData,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      throw error;
    }
  },

  // Teacher opens/activates negotiation
  async activateNegotiation(negotiationId) {
    try {
      const negotiationRef = doc(db, COLLECTIONS.TEACHER_NEGOTIATIONS, negotiationId);
      await updateDoc(negotiationRef, {
        status: 'active',
        activatedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      throw error;
    }
  },

  // Teacher accepts negotiation
  async acceptNegotiation(negotiationId) {
    try {
      const negotiationRef = doc(db, COLLECTIONS.TEACHER_NEGOTIATIONS, negotiationId);
      await updateDoc(negotiationRef, {
        status: 'accepted',
        acceptedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      throw error;
    }
  },

  // Teacher rejects negotiation
  async rejectNegotiation(negotiationId) {
    try {
      const negotiationRef = doc(db, COLLECTIONS.TEACHER_NEGOTIATIONS, negotiationId);
      await updateDoc(negotiationRef, {
        status: 'rejected',
        rejectedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      throw error;
    }
  },

  // Close negotiation (by student or teacher)
  async closeNegotiation(negotiationId) {
    try {
      const negotiationRef = doc(db, COLLECTIONS.TEACHER_NEGOTIATIONS, negotiationId);
      await updateDoc(negotiationRef, {
        status: 'closed',
        closedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      throw error;
    }
  },

  // Send message in negotiation chat
  async sendNegotiationMessage(negotiationId, messageData) {
    try {
      const docRef = await addDoc(collection(db, COLLECTIONS.NEGOTIATION_MESSAGES), {
        negotiationId,
        ...messageData,
        createdAt: serverTimestamp()
      });
      return docRef.id;
    } catch (error) {
      throw error;
    }
  },

  // Get messages for a negotiation
  async getNegotiationMessages(negotiationId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.NEGOTIATION_MESSAGES),
        where('negotiationId', '==', negotiationId),
        orderBy('createdAt', 'asc')
      );
      
      const querySnapshot = await getDocs(q);
      const messages = [];
      querySnapshot.forEach((doc) => {
        messages.push({ id: doc.id, ...doc.data() });
      });
      
      return messages;
    } catch (error) {
      throw error;
    }
  },

  // Subscribe to negotiation messages (real-time)
  subscribeToNegotiationMessages(negotiationId, callback) {
    try {
      // Try with orderBy first, fallback to without if index is missing
      let q = query(
        collection(db, COLLECTIONS.NEGOTIATION_MESSAGES),
        where('negotiationId', '==', negotiationId),
        orderBy('createdAt', 'asc')
      );
      
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const messages = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          messages.push({ id: doc.id, ...data });
        });
        // Sort by timestamp if orderBy didn't work
        messages.sort((a, b) => {
          const aTime = a.createdAt?.seconds || (a.createdAt ? new Date(a.createdAt).getTime() / 1000 : 0);
          const bTime = b.createdAt?.seconds || (b.createdAt ? new Date(b.createdAt).getTime() / 1000 : 0);
          return aTime - bTime;
        });
        callback({ success: true, data: messages });
      }, (error) => {
        // If orderBy fails (missing index), try without it
        console.warn('OrderBy failed, trying without orderBy:', error.message);
        try {
          const qFallback = query(
            collection(db, COLLECTIONS.NEGOTIATION_MESSAGES),
            where('negotiationId', '==', negotiationId)
          );
          
          return onSnapshot(qFallback, (snapshot) => {
            const messages = [];
            snapshot.forEach((doc) => {
              const data = doc.data();
              messages.push({ id: doc.id, ...data });
            });
            // Sort in memory by timestamp
            messages.sort((a, b) => {
              const aTime = a.createdAt?.seconds || (a.createdAt ? new Date(a.createdAt).getTime() / 1000 : 0);
              const bTime = b.createdAt?.seconds || (b.createdAt ? new Date(b.createdAt).getTime() / 1000 : 0);
              return aTime - bTime;
            });
            callback({ success: true, data: messages });
          }, (fallbackError) => {
            console.error('Error loading messages:', fallbackError);
            callback({ success: false, error: fallbackError.message });
          });
        } catch (fallbackError) {
          callback({ success: false, error: fallbackError.message });
          return () => {};
        }
      });
      
      return unsubscribe;
    } catch (error) {
      console.error('Error setting up message subscription:', error);
      callback({ success: false, error: error.message });
      return () => {};
    }
  },

  // Subscribe to negotiation updates (real-time)
  subscribeToNegotiation(negotiationId, callback) {
    try {
      const negotiationRef = doc(db, COLLECTIONS.TEACHER_NEGOTIATIONS, negotiationId);
      
      const unsubscribe = onSnapshot(negotiationRef, (snapshot) => {
        if (snapshot.exists()) {
          callback({ success: true, data: { id: snapshot.id, ...snapshot.data() } });
        } else {
          callback({ success: false, error: 'Negotiation not found' });
        }
      }, (error) => {
        callback({ success: false, error: error.message });
      });
      
      return unsubscribe;
    } catch (error) {
      callback({ success: false, error: error.message });
      return () => {};
    }
  }
};

export { COLLECTIONS };
